import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useSessionTracking = (userId: string | null) => {
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!userId) return;

    const startSession = async () => {
      try {
        // Update last_login_at
        await supabase
          .from("profiles")
          .update({ last_login_at: new Date().toISOString() })
          .eq("user_id", userId);

        // Create new session
        const { data, error } = await supabase
          .from("user_sessions")
          .insert({ 
            user_id: userId, 
            started_at: new Date().toISOString() 
          })
          .select("id")
          .single();

        if (data && !error) {
          sessionIdRef.current = data.id;
          startTimeRef.current = Date.now();
        }
      } catch (error) {
        console.error("Error starting session:", error);
      }
    };

    const endSession = async () => {
      if (!sessionIdRef.current) return;

      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);

      try {
        await supabase
          .from("user_sessions")
          .update({
            ended_at: new Date().toISOString(),
            duration_seconds: duration,
          })
          .eq("id", sessionIdRef.current);
      } catch (error) {
        console.error("Error ending session:", error);
      }
    };

    startSession();

    // Handle page unload
    const handleBeforeUnload = () => {
      if (!sessionIdRef.current) return;
      
      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
      
      // Use sendBeacon for reliable delivery on page unload
      const payload = JSON.stringify({
        ended_at: new Date().toISOString(),
        duration_seconds: duration,
      });
      
      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_sessions?id=eq.${sessionIdRef.current}`,
        new Blob([payload], { type: "application/json" })
      );
    };

    // Handle visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endSession();
      } else if (document.visibilityState === "visible" && !sessionIdRef.current) {
        startSession();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      endSession();
    };
  }, [userId]);
};
